import type {
  AdminConversationAssignmentRequest,
  AdminConversationHandoffRequest,
  AdminConversationInboxItem,
  AdminConversationStatusRequest,
  AdminLeadAssignee,
  ConversationCursor,
  ConversationHandoffState,
  ConversationStatus,
  IdempotencyKey,
} from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import { ContentState } from "@whitehaven/ui";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  adminConversationAuthorizationKey,
  canAccessAdminConversationInbox,
} from "./admin-conversation-authorization";
import {
  ADMIN_CONVERSATION_ASSIGNEE_PAGE_SIZE,
  ADMIN_CONVERSATION_INBOX_PAGE_SIZE,
  type AdminConversationInboxGateways,
} from "./admin-conversation-gateway";
import {
  applyConfirmedAdminConversationAssignment,
  applyConfirmedAdminConversationHandoff,
  applyConfirmedAdminConversationStatus,
  isAdminConversationAssigneeDirectoryPageConsistent,
  isAdminConversationFailureOutcome,
  isAdminConversationInboxPageConsistent,
  mergeAdminConversationInboxItems,
} from "./admin-conversation-inbox-model";
import {
  AdminConversationInboxView,
  type AdminConversationDirectoryState,
  type AdminConversationFeedback,
  type AdminConversationPendingMutation,
} from "./AdminConversationInboxView";

export const ADMIN_CONVERSATION_INBOX_PATH = "/admin/conversations" as const;

export interface AdminConversationInboxRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly gateways: AdminConversationInboxGateways | null;
}

type InboxRequestMode = "initial" | "refresh" | "load-more";

type InboxState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly items: readonly AdminConversationInboxItem[];
      readonly nextCursor: ConversationCursor | null;
      readonly consumedCursors: readonly ConversationCursor[];
      readonly refreshing: boolean;
      readonly loadingMore: boolean;
    };

type AssigneeState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "unavailable" }
  | {
      readonly kind: "ready";
      readonly items: readonly AdminLeadAssignee[];
    };

type MutationOperation =
  | {
      readonly kind: "assignment";
      readonly item: AdminConversationInboxItem;
      readonly request: AdminConversationAssignmentRequest;
    }
  | {
      readonly kind: "status";
      readonly item: AdminConversationInboxItem;
      readonly request: AdminConversationStatusRequest;
    }
  | {
      readonly kind: "handoff";
      readonly item: AdminConversationInboxItem;
      readonly request: AdminConversationHandoffRequest;
    };

function createMutationIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && /^[A-Za-z0-9_-]{8,128}$/.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

function routeFeedback(
  tone: AdminConversationFeedback["tone"],
  message: string,
): AdminConversationFeedback {
  return { tone, message };
}

function AdminConversationFrame({
  children,
}: {
  readonly children: ReactNode;
}) {
  return (
    <section
      className="portal-page admin-conversation-page portal-screen-only"
      aria-labelledby="portal-title"
      aria-describedby="admin-conversation-description"
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">CRM workspace</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Conversations
          </h1>
          <p id="admin-conversation-description">
            Review privacy-minimized conversation activity, assignment, workflow
            status, and requests for human follow-up.
          </p>
        </div>
      </header>
      <nav aria-label="Conversation workspace">
        <a href="/admin/crm/leads">Lead list</a>
        <span aria-current="page">Conversations</span>
      </nav>
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

function RouteState({
  kind,
  title,
  children,
  actions,
}: {
  readonly kind: "loading" | "warning" | "error";
  readonly title: string;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <ContentState
      kind={kind}
      title={title}
      actions={actions}
      live={kind === "error"}
    >
      {children}
    </ContentState>
  );
}

function directoryPresentation(
  state: AssigneeState,
): AdminConversationDirectoryState {
  return state.kind;
}

function mutationSuccessMessage(kind: MutationOperation["kind"]): string {
  switch (kind) {
    case "assignment":
      return "Conversation assignment confirmed.";
    case "status":
      return "Conversation status confirmed.";
    case "handoff":
      return "Human follow-up state confirmed.";
  }
}

function AuthorizedAdminConversationInbox({
  gateways,
}: {
  readonly gateways: AdminConversationInboxGateways;
}) {
  const [inbox, setInbox] = useState<InboxState>({ kind: "loading" });
  const [assignees, setAssignees] = useState<AssigneeState>(
    gateways.readAssignees && gateways.mutateAssignment
      ? { kind: "loading" }
      : { kind: "unavailable" },
  );
  const [pendingMutation, setPendingMutation] =
    useState<AdminConversationPendingMutation | null>(null);
  const [feedback, setFeedback] = useState<AdminConversationFeedback | null>(
    null,
  );
  const [refreshRequired, setRefreshRequired] = useState(false);
  const inboxSequence = useRef(0);
  const directorySequence = useRef(0);
  const mutationSequence = useRef(0);
  const activeInboxRequest = useRef<AbortController | null>(null);
  const activeDirectoryRequest = useRef<AbortController | null>(null);
  const activeMutation = useRef<AbortController | null>(null);
  const latestItems = useRef<readonly AdminConversationInboxItem[]>([]);

  const failInboxRequest = useCallback((mode: InboxRequestMode) => {
    setInbox((current) => {
      if (mode === "initial" || current.kind !== "ready") {
        latestItems.current = [];
        return { kind: "error" };
      }
      return {
        ...current,
        refreshing: false,
        loadingMore: false,
      };
    });
    setFeedback(
      routeFeedback(
        "critical",
        mode === "load-more"
          ? "More conversations could not be loaded."
          : "The conversation inbox could not be refreshed.",
      ),
    );
  }, []);

  const requestInbox = useCallback(
    async (mode: InboxRequestMode, cursor: ConversationCursor | null) => {
      if (activeMutation.current) return;

      const requestId = inboxSequence.current + 1;
      inboxSequence.current = requestId;
      activeInboxRequest.current?.abort();
      const controller = new AbortController();
      activeInboxRequest.current = controller;
      const protectedGeneration = captureProtectedStateGeneration();

      setInbox((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }
        return {
          ...current,
          refreshing: mode === "refresh",
          loadingMore: mode === "load-more",
        };
      });
      if (mode !== "load-more") setFeedback(null);

      try {
        const page = await gateways.readInbox(
          { cursor, limit: ADMIN_CONVERSATION_INBOX_PAGE_SIZE },
          { signal: controller.signal },
        );

        if (
          controller.signal.aborted ||
          inboxSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedGeneration)
        ) {
          return;
        }
        if (!isAdminConversationInboxPageConsistent(page)) {
          failInboxRequest(mode);
          return;
        }

        setInbox((current) => {
          if (mode === "load-more") {
            if (
              cursor === null ||
              current.kind !== "ready" ||
              page.nextCursor === cursor ||
              (page.nextCursor !== null &&
                current.consumedCursors.includes(page.nextCursor))
            ) {
              failInboxRequest(mode);
              return current;
            }
            const items = mergeAdminConversationInboxItems(
              current.items,
              page.items,
            );
            if (!items) {
              failInboxRequest(mode);
              return current;
            }
            latestItems.current = items;
            return {
              kind: "ready",
              items,
              nextCursor: page.nextCursor,
              consumedCursors: [...current.consumedCursors, cursor],
              refreshing: false,
              loadingMore: false,
            };
          }

          latestItems.current = page.items;
          return {
            kind: "ready",
            items: page.items,
            nextCursor: page.nextCursor,
            consumedCursors: [],
            refreshing: false,
            loadingMore: false,
          };
        });
        if (mode !== "load-more") {
          setRefreshRequired(false);
          setFeedback(null);
        }
      } catch {
        if (
          controller.signal.aborted ||
          inboxSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedGeneration)
        ) {
          return;
        }
        failInboxRequest(mode);
      } finally {
        if (activeInboxRequest.current === controller) {
          activeInboxRequest.current = null;
        }
      }
    },
    [failInboxRequest, gateways],
  );

  const requestAssignees = useCallback(async () => {
    if (!gateways.readAssignees || !gateways.mutateAssignment) {
      setAssignees({ kind: "unavailable" });
      return;
    }
    if (activeMutation.current) return;

    const requestId = directorySequence.current + 1;
    directorySequence.current = requestId;
    activeDirectoryRequest.current?.abort();
    const controller = new AbortController();
    activeDirectoryRequest.current = controller;
    const protectedGeneration = captureProtectedStateGeneration();
    setAssignees({ kind: "loading" });

    try {
      const collected: AdminLeadAssignee[] = [];
      const seenUsers = new Set<string>();
      const seenCursors = new Set<string>();
      let cursor = null;

      for (let pageNumber = 0; pageNumber < 20; pageNumber += 1) {
        const page = await gateways.readAssignees(
          { cursor, limit: ADMIN_CONVERSATION_ASSIGNEE_PAGE_SIZE },
          { signal: controller.signal },
        );

        if (
          controller.signal.aborted ||
          directorySequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedGeneration)
        ) {
          return;
        }
        if (!isAdminConversationAssigneeDirectoryPageConsistent(page)) {
          throw new Error("Invalid assignee directory page.");
        }
        for (const item of page.items) {
          if (seenUsers.has(item.userId)) {
            throw new Error("Duplicate assignee directory record.");
          }
          seenUsers.add(item.userId);
          collected.push(item);
        }
        if (page.nextCursor === null) {
          setAssignees({ kind: "ready", items: collected });
          return;
        }
        if (seenCursors.has(page.nextCursor)) {
          throw new Error("Assignee directory cursor did not advance.");
        }
        seenCursors.add(page.nextCursor);
        cursor = page.nextCursor;
      }
      throw new Error("Assignee directory exceeded its page bound.");
    } catch {
      if (
        controller.signal.aborted ||
        directorySequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(protectedGeneration)
      ) {
        return;
      }
      setAssignees({ kind: "error" });
    } finally {
      if (activeDirectoryRequest.current === controller) {
        activeDirectoryRequest.current = null;
      }
    }
  }, [gateways]);

  const requireRefresh = useCallback((message: string) => {
    setRefreshRequired(true);
    setFeedback(routeFeedback("critical", message));
  }, []);

  const executeMutation = useCallback(
    async (operation: MutationOperation) => {
      if (
        activeMutation.current ||
        activeInboxRequest.current ||
        refreshRequired
      ) {
        return;
      }

      const idempotencyKey = createMutationIdempotencyKey();
      if (!idempotencyKey) {
        requireRefresh(
          "The conversation change could not be prepared. Refresh before trying again.",
        );
        return;
      }

      const requestId = mutationSequence.current + 1;
      mutationSequence.current = requestId;
      const controller = new AbortController();
      activeMutation.current = controller;
      const protectedGeneration = captureProtectedStateGeneration();
      setPendingMutation({
        conversationId: operation.item.conversationId,
        kind: operation.kind,
      });
      setFeedback(null);

      try {
        let updated: AdminConversationInboxItem | null = null;
        let failed = false;

        switch (operation.kind) {
          case "assignment": {
            if (!gateways.mutateAssignment) return;
            const outcome = await gateways.mutateAssignment(operation.request, {
              signal: controller.signal,
              idempotencyKey,
            });
            if (isAdminConversationFailureOutcome(outcome)) {
              failed = true;
            } else {
              const current =
                latestItems.current.find(
                  ({ conversationId }) =>
                    conversationId === operation.item.conversationId,
                ) ?? operation.item;
              updated = applyConfirmedAdminConversationAssignment(
                current,
                operation.request,
                outcome,
              );
            }
            break;
          }
          case "status": {
            if (!gateways.mutateStatus) return;
            const outcome = await gateways.mutateStatus(operation.request, {
              signal: controller.signal,
              idempotencyKey,
            });
            if (isAdminConversationFailureOutcome(outcome)) {
              failed = true;
            } else {
              const current =
                latestItems.current.find(
                  ({ conversationId }) =>
                    conversationId === operation.item.conversationId,
                ) ?? operation.item;
              updated = applyConfirmedAdminConversationStatus(
                current,
                operation.request,
                outcome,
              );
            }
            break;
          }
          case "handoff": {
            if (!gateways.mutateHandoff) return;
            const outcome = await gateways.mutateHandoff(operation.request, {
              signal: controller.signal,
              idempotencyKey,
            });
            if (isAdminConversationFailureOutcome(outcome)) {
              failed = true;
            } else {
              const current =
                latestItems.current.find(
                  ({ conversationId }) =>
                    conversationId === operation.item.conversationId,
                ) ?? operation.item;
              updated = applyConfirmedAdminConversationHandoff(
                current,
                operation.request,
                outcome,
              );
            }
            break;
          }
        }

        if (
          controller.signal.aborted ||
          mutationSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedGeneration)
        ) {
          return;
        }
        if (failed || !updated) {
          requireRefresh(
            failed
              ? "This conversation changed elsewhere or the change was rejected. Refresh before trying again."
              : "The mutation response could not be verified. Refresh before trying again.",
          );
          return;
        }

        setInbox((current) => {
          if (current.kind !== "ready") return current;
          const items = current.items.map((item) =>
            item.conversationId === updated!.conversationId ? updated! : item,
          );
          latestItems.current = items;
          return { ...current, items };
        });
        setFeedback(
          routeFeedback("positive", mutationSuccessMessage(operation.kind)),
        );
      } catch {
        if (
          controller.signal.aborted ||
          mutationSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedGeneration)
        ) {
          return;
        }
        requireRefresh(
          "The conversation change could not be confirmed. Refresh before trying again.",
        );
      } finally {
        if (
          mutationSequence.current === requestId &&
          isProtectedStateGenerationCurrent(protectedGeneration)
        ) {
          activeMutation.current = null;
          setPendingMutation(null);
        }
      }
    },
    [gateways, refreshRequired, requireRefresh],
  );

  useEffect(() => {
    const unregister = registerProtectedStateCleaner(
      "chat-admin-conversation-inbox",
      () => {
        inboxSequence.current += 1;
        directorySequence.current += 1;
        mutationSequence.current += 1;
        activeInboxRequest.current?.abort();
        activeDirectoryRequest.current?.abort();
        activeMutation.current?.abort();
        activeInboxRequest.current = null;
        activeDirectoryRequest.current = null;
        activeMutation.current = null;
        latestItems.current = [];
        setInbox({ kind: "loading" });
        setAssignees(
          gateways.readAssignees && gateways.mutateAssignment
            ? { kind: "loading" }
            : { kind: "unavailable" },
        );
        setPendingMutation(null);
        setFeedback(null);
        setRefreshRequired(false);
      },
    );

    void requestInbox("initial", null);
    void requestAssignees();

    return () => {
      unregister();
      inboxSequence.current += 1;
      directorySequence.current += 1;
      mutationSequence.current += 1;
      activeInboxRequest.current?.abort();
      activeDirectoryRequest.current?.abort();
      activeMutation.current?.abort();
      activeInboxRequest.current = null;
      activeDirectoryRequest.current = null;
      activeMutation.current = null;
      latestItems.current = [];
    };
  }, [gateways, requestAssignees, requestInbox]);

  if (inbox.kind === "loading") {
    return (
      <RouteState kind="loading" title="Loading authorized conversations.">
        <p>The protected conversation inbox is being prepared.</p>
      </RouteState>
    );
  }

  if (inbox.kind === "error") {
    return (
      <RouteState
        kind="error"
        title="The conversation inbox could not be loaded."
        actions={
          <button
            type="button"
            onClick={() => void requestInbox("initial", null)}
          >
            Try again
          </button>
        }
      >
        <p>
          No conversation metadata is shown. Retry the authorized read or return
          to portal home.
        </p>
      </RouteState>
    );
  }

  const assigneeItems = assignees.kind === "ready" ? assignees.items : [];

  return (
    <AdminConversationInboxView
      items={inbox.items}
      assignees={assigneeItems}
      directoryState={directoryPresentation(assignees)}
      nextCursor={inbox.nextCursor}
      refreshing={inbox.refreshing}
      loadingMore={inbox.loadingMore}
      pendingMutation={pendingMutation}
      refreshRequired={refreshRequired}
      feedback={feedback}
      canMutateAssignment={
        assignees.kind === "ready" && gateways.mutateAssignment !== null
      }
      canMutateStatus={gateways.mutateStatus !== null}
      canMutateHandoff={gateways.mutateHandoff !== null}
      onRefresh={() => void requestInbox("refresh", null)}
      onLoadMore={() => {
        if (inbox.nextCursor) {
          void requestInbox("load-more", inbox.nextCursor);
        }
      }}
      onRetryAssignees={() => void requestAssignees()}
      onAssign={(item, targetUserId) => {
        if (!gateways.mutateAssignment) return;
        void executeMutation({
          kind: "assignment",
          item,
          request: {
            conversationId: item.conversationId,
            expectedAssigneeUserId: item.assignedUserId,
            targetAssigneeUserId: targetUserId,
            expectedAssignmentRevision: item.assignmentRevision,
            expectedUpdatedAt: item.updatedAt,
          },
        });
      }}
      onStatusChange={(item, targetStatus) => {
        if (!gateways.mutateStatus) return;
        void executeMutation({
          kind: "status",
          item,
          request: {
            conversationId: item.conversationId,
            expectedStatus: item.status,
            targetStatus,
            expectedStatusRevision: item.statusRevision,
            expectedUpdatedAt: item.updatedAt,
          },
        });
      }}
      onHandoffChange={(item, targetState) => {
        if (!gateways.mutateHandoff) return;
        void executeMutation({
          kind: "handoff",
          item,
          request: {
            conversationId: item.conversationId,
            expectedState: item.handoffState,
            targetState,
            expectedHandoffRevision: item.handoffRevision,
            expectedUpdatedAt: item.updatedAt,
          },
        });
      }}
    />
  );
}

export function AdminConversationInboxRoute({
  subject,
  gateways,
}: AdminConversationInboxRouteProps) {
  if (!subject) {
    return (
      <AdminConversationFrame>
        <RouteState
          kind="warning"
          title="Secure sign-in is required."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            Conversation metadata stays unavailable until a verified portal
            session supplies an authorized account.
          </p>
        </RouteState>
      </AdminConversationFrame>
    );
  }

  if (!canAccessAdminConversationInbox(subject)) {
    return (
      <AdminConversationFrame>
        <RouteState
          kind="warning"
          title="This account cannot open the conversation inbox."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            No conversation information was requested or displayed. Use an
            approved account with CRM access.
          </p>
        </RouteState>
      </AdminConversationFrame>
    );
  }

  if (!gateways) {
    return (
      <AdminConversationFrame>
        <RouteState
          kind="warning"
          title="The secure conversation connection is not available yet."
          actions={<a href="/admin/crm/leads">Return to lead list</a>}
        >
          <p>
            The permission gate is ready, but protected conversation services
            have not been connected. No sample or cached conversations are
            shown.
          </p>
        </RouteState>
      </AdminConversationFrame>
    );
  }

  const authorizedGateways = canAccessAdminConversationInbox(subject, "write")
    ? gateways
    : {
        ...gateways,
        readAssignees: null,
        mutateAssignment: null,
        mutateStatus: null,
        mutateHandoff: null,
      };

  return (
    <AdminConversationFrame>
      <AuthorizedAdminConversationInbox
        key={adminConversationAuthorizationKey(subject)}
        gateways={authorizedGateways}
      />
    </AdminConversationFrame>
  );
}
